import { useRef, type ReactNode } from 'react';

import { Spinner } from '../../atoms/Spinner';
import { cn } from '../../lib/utils';
import {
  AccessLegitimacyFilters,
  type AccessLegitimacyShowing,
  type SensitivityFilterOption,
} from '../../molecules/AccessLegitimacyFilters';
import type { FilterDropdownOption } from '../../molecules/FilterDropdown';
import { AccessLegitimacyDetailDrawer } from '../AccessLegitimacyDetailDrawer';
import type { AccessLegitimacyDetailDrawerLabels } from '../AccessLegitimacyDetailDrawer/types';
import {
  AccessLegitimacyGrid,
  type AccessLegitimacyEvent,
  type AccessLegitimacySortDir,
} from '../AccessLegitimacyGrid';

const count = (n: number, noun: string): string =>
  `${n.toLocaleString('en-US')} ${noun}${n === 1 ? '' : 's'}`;

/** "N events · M files"; "N events" alone where the grid has no file count. */
const DEFAULT_SHOWING_TEMPLATE = ({ total, files }: AccessLegitimacyShowing): string =>
  files === undefined
    ? count(total, 'event')
    : `${count(total, 'event')} · ${count(files, 'file')}`;
const DEFAULT_USER_TYPE_BASE_LABEL = 'User type';
const DEFAULT_FILE_TYPE_BASE_LABEL = 'File type';
const DEFAULT_FILTER_EMPTY_MESSAGE = 'No events match the current filters.';
const DEFAULT_TOPIC_POPOVER_TITLE = (n: number): string => `Document Topic (${n})`;
const DEFAULT_TOPIC_CHIP_ARIA_LABEL = (n: number): string => `plus ${n} more topics`;
const DEFAULT_SENSITIVITY_RADIOGROUP_ARIA_LABEL = 'Filter by sensitivity';

export interface AccessLegitimacyBodyProps {
  /**
   * Optional widget heading rendered inside the cream header region. Omitted by
   * default — the Access Legitimacy page surfaces the title in the app-header
   * chrome, so the body renders no heading unless a consumer passes one.
   */
  title?: string;

  /**
   * When `true`, the results area is dimmed (and non-interactive) with a spinner
   * overlay while a filter / search / sort / page change refetches — the prior
   * page stays visible underneath via `keepPreviousData`. The filter controls
   * stay live so the user can keep narrowing. Defaults to `false`.
   */
  isRefetching?: boolean;

  // --- Server-driven data (controlled; design v6 §3.3) ---------------------
  /** The current page of rows (server already filtered / sorted / paged). */
  rows: ReadonlyArray<AccessLegitimacyEvent>;
  /** Total filtered row count, pre-paging (the "N events" indicator). */
  total: number;
  /** Distinct files behind `total`, as the cards count them; absent unless anchored to a card. */
  files?: number;
  /** Sensitivity pill options ("All Sensitivities" first, then catalog order). */
  sensitivityOptions: ReadonlyArray<SensitivityFilterOption>;
  /** User-type dropdown options (server-derived, icons resolved by the app). */
  userTypeOptions: ReadonlyArray<FilterDropdownOption>;
  /** File-type dropdown options (server-derived). */
  fileTypeOptions: ReadonlyArray<FilterDropdownOption>;

  // --- Controlled filter / sort / page state -------------------------------
  selectedSensitivity: string;
  onSensitivityChange: (next: string) => void;
  selectedUserTypes: readonly string[];
  onUserTypesChange: (next: readonly string[]) => void;
  selectedFileTypes: readonly string[];
  onFileTypesChange: (next: readonly string[]) => void;
  /** Live search input value (the body does not debounce — the container does). */
  searchQuery: string;
  onSearchChange: (next: string) => void;
  searchPlaceholder?: string;
  sortDir: AccessLegitimacySortDir;
  onSortDirChange: (next: AccessLegitimacySortDir) => void;
  /** 1-indexed page. */
  page: number;
  pageCount: number;
  onPageChange: (next: number) => void;

  // --- Selection / drawer --------------------------------------------------
  /** Currently-selected event id (drives the selected-row visual + open drawer). */
  selectedEventId: string | null;
  /** Fires with the next selected id, or `null` to close. */
  onSelectEvent: (id: string | null) => void;

  // --- Rendering helpers ---------------------------------------------------
  /** Formats an ISO date string for the grid + drawer. */
  formatDateTime: (iso: string) => string;
  showingTemplate?: (args: AccessLegitimacyShowing) => string;
  userTypeBaseLabel?: string;
  fileTypeBaseLabel?: string;
  /** Shown in the table region when the current filter set yields no rows. */
  filterEmptyMessage?: string;
  sensitivityRadiogroupAriaLabel?: string;
  topicPopoverTitle?: (totalCount: number) => string;
  topicChipAriaLabel?: (additionalCount: number) => string;
  drawerLabels?: AccessLegitimacyDetailDrawerLabels;
  /**
   * Slot rendered beneath the filter row for scope the container applied from
   * outside the grid's own controls (e.g. the Access Discovery node a user
   * arrived from). The container owns the chips and their removal.
   */
  scopeChips?: ReactNode;
  className?: string;
}

/**
 * AccessLegitimacyBody — the populated organism for the Access Legitimacy
 * widget, now a **pure controlled renderer** (design v6 §3.3, ADR 0001). The
 * page-level container owns all filter / sort / page / selected-event state and
 * the request lifecycle; the body just renders the filter rail, the grid, and
 * the detail drawer from props.
 *
 * The filter rail is always visible. When the current filter set yields no rows
 * (`rows` empty), the table region shows `filterEmptyMessage` so the analyst can
 * still adjust filters (the container decides dataset-empty vs the populated
 * body; this body never owns that distinction beyond the in-region message).
 */
export function AccessLegitimacyBody({
  title,
  isRefetching = false,
  rows,
  total,
  files,
  sensitivityOptions,
  userTypeOptions,
  fileTypeOptions,
  selectedSensitivity,
  onSensitivityChange,
  selectedUserTypes,
  onUserTypesChange,
  selectedFileTypes,
  onFileTypesChange,
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  sortDir,
  onSortDirChange,
  page,
  pageCount,
  onPageChange,
  selectedEventId,
  onSelectEvent,
  formatDateTime,
  showingTemplate = DEFAULT_SHOWING_TEMPLATE,
  userTypeBaseLabel = DEFAULT_USER_TYPE_BASE_LABEL,
  fileTypeBaseLabel = DEFAULT_FILE_TYPE_BASE_LABEL,
  filterEmptyMessage = DEFAULT_FILTER_EMPTY_MESSAGE,
  sensitivityRadiogroupAriaLabel = DEFAULT_SENSITIVITY_RADIOGROUP_ARIA_LABEL,
  topicPopoverTitle = DEFAULT_TOPIC_POPOVER_TITLE,
  topicChipAriaLabel = DEFAULT_TOPIC_CHIP_ARIA_LABEL,
  drawerLabels,
  scopeChips,
  className,
}: AccessLegitimacyBodyProps) {
  // The drawer payload is carried inline on each row — no second request
  // (ADR 0001). Synchronous lookup over the visible page.
  const liveEvent =
    selectedEventId !== null ? rows.find((r) => r.id === selectedEventId) : undefined;

  // Retain the most recently selected event across the drawer's close
  // animation: when `selectedEventId` resets to `null` the drawer slides out
  // while Radix keeps Content mounted, and the body still consults `event`.
  const lastEventRef = useRef<AccessLegitimacyEvent | undefined>(undefined);
  if (liveEvent !== undefined) {
    lastEventRef.current = liveEvent;
  }
  const selectedEvent = liveEvent ?? lastEventRef.current;

  const handleRowClick = (id: string) => onSelectEvent(id === selectedEventId ? null : id);

  const filterEmpty = rows.length === 0;

  return (
    // Height-bounded flex column (TDEV-960): when an ancestor caps the height
    // the rail and the pager hold their place and only the grid's rows scroll.
    // Unbounded (Storybook default, plain block flow) it sizes to content as before.
    <div
      className={cn('flex min-h-0 flex-1 flex-col', className)}
      data-testid="access-legitimacy-body"
    >
      <div
        className="shrink-0 bg-dawn-cream pb-5 pt-5"
        data-testid="access-legitimacy-filters-region"
      >
        {title ? (
          <h2
            className="mb-5 font-outfit text-[24px] font-bold capitalize leading-[1.2] text-dawn-black"
            data-testid="access-legitimacy-title"
          >
            {title}
          </h2>
        ) : null}
        <AccessLegitimacyFilters
          selectedSensitivity={selectedSensitivity}
          onSensitivityChange={onSensitivityChange}
          sensitivityOptions={sensitivityOptions}
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
          {...(searchPlaceholder !== undefined ? { searchPlaceholder } : {})}
          userTypeOptions={userTypeOptions}
          selectedUserTypes={selectedUserTypes}
          onUserTypesChange={onUserTypesChange}
          userTypeBaseLabel={userTypeBaseLabel}
          fileTypeOptions={fileTypeOptions}
          selectedFileTypes={selectedFileTypes}
          onFileTypesChange={onFileTypesChange}
          fileTypeBaseLabel={fileTypeBaseLabel}
          sensitivityRadiogroupAriaLabel={sensitivityRadiogroupAriaLabel}
          showing={{ visible: rows.length, total, files }}
          showingTemplate={showingTemplate}
        />
        {scopeChips ? <div className="mt-3">{scopeChips}</div> : null}
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col px-5 pb-5 pt-5">
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col transition-opacity',
            isRefetching && 'pointer-events-none opacity-60'
          )}
          aria-busy={isRefetching}
        >
          {filterEmpty ? (
            <p
              data-testid="access-legitimacy-filter-empty"
              className="py-6 text-center text-[13px] leading-[1.3] text-grey-400"
            >
              {filterEmptyMessage}
            </p>
          ) : (
            <AccessLegitimacyGrid
              rows={rows}
              total={total}
              sortDir={sortDir}
              onSortDirChange={onSortDirChange}
              page={page}
              pageCount={pageCount}
              onPageChange={onPageChange}
              formatDateTime={formatDateTime}
              topicPopoverTitle={topicPopoverTitle}
              topicChipAriaLabel={topicChipAriaLabel}
              selectedEventId={selectedEventId}
              onRowClick={handleRowClick}
            />
          )}
        </div>
        {isRefetching ? (
          <div
            className="absolute inset-0 flex items-start justify-center pt-10"
            data-testid="access-legitimacy-loading-overlay"
          >
            <Spinner animate size={40} />
            <span className="sr-only">Updating results…</span>
          </div>
        ) : null}
      </div>

      <AccessLegitimacyDetailDrawer
        event={selectedEvent}
        open={selectedEventId !== null}
        onClose={() => onSelectEvent(null)}
        formatDateTime={formatDateTime}
        labels={drawerLabels}
      />
    </div>
  );
}

AccessLegitimacyBody.displayName = 'AccessLegitimacyBody';
